import React from "react";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardHeader,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ShieldCheck, X } from "@phosphor-icons/react";
import type { ActiveDocumentOverride } from "hooks/api/documentGeneration/useGetDocumentChecklistStatus";
import { DocumentTypeConfig } from "./documentTypes";
import OverrideAlert from "./OverrideAlert";

export interface DocumentTypeWithStatus {
  config: DocumentTypeConfig;
  allDataAvailable: boolean;
}

interface SelectDocumentTypeStepProps {
  /** Full name of the client, displayed in the subtitle */
  clientName: string;
  /** Whether the status check is in progress */
  isLoading: boolean;
  /** Document types with their current status */
  documentTypes: DocumentTypeWithStatus[];
  /** The client's active override, or null when none is held */
  activeOverride?: ActiveDocumentOverride | null;
  /** Callback when a document type is selected */
  onSelectDocumentType: (typeKey: string) => void;
  /** Callback to close the dialog */
  onClose: () => void;
}

const cardHeaderSx = {
  p: 5,
  "& .MuiCardHeader-action": { alignSelf: "center", mt: 0, mr: 0 },
};

/**
 * First step of the Generate Document dialog.
 * Displays available document types and lets the user select one to proceed.
 *
 * @param props
 * @param props.clientName - Full name of the client
 * @param props.isLoading - Whether the status check is in progress
 * @param props.documentTypes - Document types with their current status
 * @param props.activeOverride - The client's active override, or null
 * @param props.onSelectDocumentType - Callback when a document type is selected
 * @param props.onClose - Callback to close the dialog
 */
const SelectDocumentTypeStep: React.FC<SelectDocumentTypeStepProps> = ({
  clientName,
  isLoading,
  documentTypes,
  activeOverride = null,
  onSelectDocumentType,
  onClose,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 11 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 7,
          py: 5,
        }}
      >
        <Box>
          <Typography variant="h4" component="h2">
            Generate Document
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a document type to generate for {clientName}.
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close dialog" size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 7, pt: 0, pb: 7 }}>
        <Stack sx={{ mt: 7 }}>
          {documentTypes.map(({ config, allDataAvailable }) => {
            const DocIcon = config.icon;
            const isOverridden = activeOverride?.documentType === config.key;

            return (
              <React.Fragment key={config.key}>
                <Card elevation={0} sx={{ borderRadius: 4 }}>
                  <CardActionArea
                    onClick={() => onSelectDocumentType(config.key)}
                    aria-label={`Select ${config.title}`}
                  >
                    <CardHeader
                      sx={cardHeaderSx}
                      titleTypographyProps={{ variant: "body1" }}
                      subheaderTypographyProps={{ variant: "body2" }}
                      avatar={
                        <Avatar sx={{ bgcolor: "primary.background", width: 32, height: 32 }}>
                          <Box sx={{ color: "primary.main", display: "flex" }}>
                            <DocIcon size={20} weight="duotone" />
                          </Box>
                        </Avatar>
                      }
                      title={config.title}
                      subheader={config.cardDescription}
                      action={
                        isOverridden ? (
                          <Box
                            role="img"
                            aria-label="Requirement override active"
                            sx={{ color: "warning.main", display: "flex" }}
                          >
                            <ShieldCheck size={24} weight="duotone" />
                          </Box>
                        ) : allDataAvailable ? (
                          <Chip
                            label="Ready"
                            size="small"
                            variant="outlined"
                            color="success"
                          />
                        ) : (
                          <Chip
                            label="Incomplete"
                            size="small"
                            variant="outlined"
                            color="warning"
                            sx={{ bgcolor: "orange.50" }}
                          />
                        )
                      }
                    />
                  </CardActionArea>
                </Card>
                {isOverridden && (
                  <OverrideAlert
                    grantedByName={activeOverride?.grantedByName ?? null}
                    documentTitle={config.title}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Stack>
      </DialogContent>
    </>
  );
};

export default SelectDocumentTypeStep;
